import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Users, MessageSquare, Megaphone, LifeBuoy, LogOut, Menu, X, ArrowLeft, Building2, SearchCheck } from "lucide-react";
import { useState } from "react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/users", label: "Usuários", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/auditoria", label: "Auditoria Inteligente", icon: SearchCheck, testid: "admin-nav-audit" },
  { to: "/admin/tickets", label: "Suporte", icon: LifeBuoy, testid: "admin-nav-tickets" },
  { to: "/admin/broadcast", label: "Comunicado", icon: Megaphone, testid: "admin-nav-broadcast" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-[#0F1F1A] text-stone-100">
      {/* Mobile bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#0F1F1A]/95 backdrop-blur border-b border-white/10 px-4 h-14 flex items-center justify-between">
        <button data-testid="admin-mobile-toggle" onClick={() => setOpen(!open)} className="p-2 -ml-2 text-white">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <Link to="/admin" className="font-heading font-bold text-lg text-white">Admin</Link>
        <div className="w-9 h-9 rounded-full bg-[#D4651A] text-white flex items-center justify-center font-semibold text-sm">
          {user?.name?.[0]?.toUpperCase()}
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-30 h-screen w-72 bg-[#0A1813] border-r border-white/10
        transform transition-transform lg:transform-none
        ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <Link to="/admin" onClick={() => setOpen(false)} className="block">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4651A] font-bold mb-1">Bastidor</div>
            <div className="font-heading text-xl font-extrabold text-white tracking-tight">
              estatebroker<span className="text-[#3B9EFF]">.AI</span> · Admin
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {nav.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={testid}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition
                ${isActive ? 'bg-[#D4651A] text-white' : 'text-stone-300 hover:bg-white/5'}
              `}
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <Link
            to="/app"
            data-testid="admin-back-to-app"
            className="flex items-center gap-2 text-xs text-stone-400 hover:text-white px-2 py-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar pro painel do corretor
          </Link>
          {user?.agency_id && (
            <Link
              to="/imobiliaria"
              data-testid="admin-back-to-agency"
              className="flex items-center gap-2 text-xs text-stone-400 hover:text-white px-2 py-1.5"
            >
              <Building2 className="w-3.5 h-3.5" /> Voltar pro painel da imobiliária
            </Link>
          )}
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-10 h-10 rounded-full bg-[#D4651A] text-white flex items-center justify-center font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-white">{user?.name}</div>
              <div className="text-xs text-stone-400 truncate">administrador</div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="w-full justify-start text-stone-300 hover:text-red-400 hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      {open && <div className="lg:hidden fixed inset-0 z-20 bg-black/50" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0 bg-[#FAFAF8] text-[#1C1C19]">
        <Outlet />
      </main>
    </div>
  );
}
